
import { useState, useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import DocumentUpload from "./DocumentUpload";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { handleS3ImageUploads } from "@/lib/handle-image-uploads";
import { User, CreditCard, MapPin } from "lucide-react";
import { toast } from "sonner";

import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

const SettingsForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    smsNotifications: true,
    serviceTown: "",
    serviceCenterLat: 0,
    serviceCenterLng: 0,
    serviceRadiusKm: 10,
    image: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const serviceAreaAutoRef = useRef<google.maps.places.Autocomplete | null>(null);
  const { isLoaded: serviceLoaded, loadError: serviceLoadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  useEffect(() => {
    fetch('/api/user')
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error);
        } else {
          setFormData((prev) => ({
            ...prev,
            name: data.name || "",
            email: data.email || "",
            smsNotifications: data.smsNotifications ?? false,
            serviceTown: data.serviceTown || "",
            serviceCenterLat: data.serviceCenterLat ?? 0,
            serviceCenterLng: data.serviceCenterLng ?? 0,
            serviceRadiusKm: data.serviceRadiusKm ?? 10,
            image: data.image || "",
          }));
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-10 w-48 mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} className="h-6 w-full" />
          ))}
        </div>
      </>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData({ ...formData, smsNotifications: checked });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const res = await fetch('/api/user', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          smsNotifications: formData.smsNotifications,
          serviceTown: formData.serviceTown,
          serviceCenterLat: formData.serviceCenterLat,
          serviceCenterLng: formData.serviceCenterLng,
          serviceRadiusKm: formData.serviceRadiusKm,
          image: formData.image,
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }),
      });
      const result = await res.json();
      if (!res.ok || result.error) {
        toast.error(result.error || 'Failed to save settings');
      } else {
        toast.success('Settings saved successfully!');
      }
    } catch {
      toast.error('An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStripeOnboarding = async () => {
    try {
      const response = await fetch('/api/onboard-driver', { method: 'POST' });
      const data = await response.json();
      if (!response.ok || !data.url) {
        console.error('Error creating Stripe onboarding link:', data.error);
        toast.error(data.error || 'Failed to create Stripe onboarding link');
        return;
      }
      window.location.assign(data.url);
    } catch {
      console.error('Unexpected error during Stripe onboarding');
      toast.error('Failed to create Stripe onboarding link');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center space-x-4">
        <Avatar>
          {formData.image ? (
            <AvatarImage src={formData.image} alt="Profile photo" />
          ) : (
            <AvatarFallback>
              <User className="h-6 w-6 text-muted-foreground" />
            </AvatarFallback>
          )}
        </Avatar>
        <Input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            if (e.target.files?.[0]) {
              const file = e.target.files[0];
              const [url] = await handleS3ImageUploads([file]);
              if (url) {
                setFormData((prev) => ({ ...prev, image: url }));
                toast.success("Profile photo uploaded");
              } else {
                toast.error("Failed to upload photo");
              }
            }
          }}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column - User Info */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
          
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          
          <div className="flex items-center justify-between space-x-2">
            <Label htmlFor="smsNotifications">SMS Notifications</Label>
            <Switch
              id="smsNotifications"
              checked={formData.smsNotifications}
              onCheckedChange={handleSwitchChange}
            />
          </div>

          <div className="space-y-2 border-b pb-4">
            <Label htmlFor="serviceTown">Service Area City</Label>
            {serviceLoaded && !serviceLoadError ? (
              <Autocomplete
                onLoad={(autocomplete) => (serviceAreaAutoRef.current = autocomplete)}
                onPlaceChanged={() => {
                  const place = serviceAreaAutoRef.current?.getPlace();
                  if (place?.geometry?.location) {
                    const formatted = place.formatted_address || "";
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    setFormData((prev) => ({
                      ...prev,
                      serviceTown: formatted,
                      serviceCenterLat: lat,
                      serviceCenterLng: lng,
                    }));
                  }
                }}
                options={{ types: ["(cities)"], componentRestrictions: { country: "uk" } }}
              >
                <Input
                  id="serviceTown"
                  name="serviceTown"
                  placeholder="Enter a city"
                  value={formData.serviceTown}
                  onChange={handleInputChange}
                />
              </Autocomplete>
            ) : (
              <Input
                id="serviceTown"
                name="serviceTown"
                placeholder="Enter a city"
                value={formData.serviceTown}
                onChange={handleInputChange}
              />
            )}
          </div>

          <div className="flex items-center justify-between border-b pb-4">
            <Label htmlFor="serviceRadiusKm">Service Radius</Label>
            <Select
              id="serviceRadiusKm"
              value={formData.serviceRadiusKm.toString()}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  serviceRadiusKm: parseInt(value, 10),
                }))
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Radius" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 km</SelectItem>
                <SelectItem value="30">30 km</SelectItem>
                <SelectItem value="50">50 km</SelectItem>
                <SelectItem value="80">80 km</SelectItem>
                <SelectItem value="100">100 km</SelectItem>
              </SelectContent>
            </Select>
          </div>

        {/* Stripe Onboarding */}
        <div className="mt-4">
          <p className="text-sm text-gray-500 dark:text-muted-foreground mb-2">
            Sign up with Stripe to post quotes
          </p>
          <Button onClick={handleStripeOnboarding} className="w-full md:w-auto flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Sign up with Stripe
          </Button>
        </div>
        </div>
        
        {/* Right Column - Document Uploads */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold mb-4">Document Uploads</h3>
          
          <DocumentUpload
            id="license"
            label="Driver's License"
            description="Upload a clear photo of your driver's license"
          />
          
          <DocumentUpload
            id="insurance"
            label="Insurance Document"
            description="Upload your current insurance certificate"
          />
          
          <DocumentUpload
            id="utility"
            label="Utility Bill"
            description="Upload a recent utility bill for address verification"
          />
        </div>
      </div>

      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold mb-4">Change Password</h3>

        <div className="space-y-2">
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            id="current-password"
            name="currentPassword"
            type="password"
            value={formData.currentPassword}
            onChange={handleInputChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <Input
            id="new-password"
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleInputChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <Button type="submit" className="w-full md:w-auto" disabled={isSaving || isLoading}>
        {isSaving ? 'Saving...' : 'Save Settings'}
      </Button>
    </form>
  );
};

export default SettingsForm;
